import type { NoticeRow, Preview, Session, Stats } from './types';

/**
 * Dati finti, dietro `VITE_FIXTURE=1`.
 *
 * Stesso pattern di `EXPO_PUBLIC_AVVISI_FIXTURE` in am-mobile (docs/11 §7.2), e
 * per la stessa ragione: le superfici devono essere complete e rivedibili senza
 * aspettare che qualcuno abiliti un ente vero su Supabase. È anche l'unico modo
 * di guardare l'allerta ufficiale in corso, la quota esaurita o il sollecito di
 * revisione senza costruirsi lo stato a mano nel database.
 *
 * **Non è una modalità di test in produzione.** La variabile è build-time: un
 * build senza `VITE_FIXTURE` non contiene questo percorso, e quando è attiva la
 * console mostra una fascia gialla in cima che non si può non vedere.
 * L'anteprima qui è composta dal client — quindi in fixture *mente per
 * costruzione*, al contrario di quella vera che arriva dal server.
 */

export const fixtureOn = import.meta.env.VITE_FIXTURE === '1';

const hoursFromNow = (h: number) => new Date(Date.now() + h * 3_600_000).toISOString();

const SESSION: Session = {
  istat: '015146',
  display_name: 'Comune di Sesto San Giovanni',
  is_active: true,
  suspended_reason: '',
  operator_name: 'M. Rossi, Protezione Civile',
  console_enabled: true,
  limits: {
    title_max: 80,
    body_max: 500,
    default_ttl_hours: 24,
    max_ttl_hours: 72,
    stale_hours: 24,
  },
  quota: {
    daily_used: 1,
    daily_cap: 6,
    weekly_used: 3,
    weekly_cap: 4,
    alert_boost: true,
  },
  activity: { today: 2, week: 5, month: 14 },
  recipients: 4212,
};

const NOTICES: NoticeRow[] = [
  {
    id: '11111111-1111-4111-8111-111111111111',
    title: 'Scuole chiuse domani',
    body:
      'Tutte le scuole di ogni ordine e grado resteranno chiuse giovedì 14 agosto.\n' +
      'I servizi educativi comunali sono sospesi per la stessa giornata.',
    link_url: 'https://comune.esempio.it/ordinanza-42',
    link_label: 'Leggi l’ordinanza',
    published_at: hoursFromNow(-3),
    expires_at: hoursFromNow(20),
    deleted_at: null,
    push_requested: true,
    revokes_id: null,
    actor: 'console:M. Rossi, Protezione Civile',
    stato: 'attivo',
    needs_review: false,
  },
  {
    // Il caso che il sollecito esiste per intercettare.
    id: '22222222-2222-4222-8222-222222222222',
    title: 'Acqua non potabile in zona Rondò',
    body:
      'L’acqua del civico acquedotto non è potabile nelle vie Marconi, Verdi e ' +
      'limitrofe. Non usarla per bere né per cucinare fino a nuova comunicazione.',
    link_url: null,
    link_label: null,
    published_at: hoursFromNow(-26),
    expires_at: hoursFromNow(6),
    deleted_at: null,
    push_requested: true,
    revokes_id: null,
    actor: 'console:M. Rossi, Protezione Civile',
    stato: 'attivo',
    needs_review: true,
  },
  {
    id: '33333333-3333-4333-8333-333333333333',
    title: 'Riaperto il sottopasso di via Bergamo',
    body: 'Il sottopasso è tornato transitabile in entrambe le direzioni.',
    link_url: null,
    link_label: null,
    published_at: hoursFromNow(-50),
    expires_at: hoursFromNow(-26),
    deleted_at: null,
    push_requested: true,
    revokes_id: '44444444-4444-4444-8444-444444444444',
    actor: 'console:G. Bianchi',
    stato: 'scaduto',
    needs_review: false,
  },
  {
    id: '44444444-4444-4444-8444-444444444444',
    title: 'Sottopasso di via Bergamo interrotto',
    body: 'Allagamento in corso. Il sottopasso è chiuso al transito.',
    link_url: null,
    link_label: null,
    published_at: hoursFromNow(-54),
    expires_at: hoursFromNow(-50),
    deleted_at: null,
    push_requested: true,
    revokes_id: null,
    actor: 'console:G. Bianchi',
    stato: 'scaduto',
    needs_review: false,
  },
  {
    id: '55555555-5555-4555-8555-555555555555',
    title: 'Punto di raccolta in piazza Petazzi',
    body: 'Refuso: pubblicato per errore durante una prova.',
    link_url: null,
    link_label: null,
    published_at: hoursFromNow(-72),
    expires_at: hoursFromNow(-48),
    deleted_at: hoursFromNow(-71),
    push_requested: false,
    revokes_id: null,
    actor: 'console:M. Rossi, Protezione Civile',
    stato: 'ritirato',
    needs_review: false,
  },
];

const STATS: Stats = {
  available: true,
  devices: 4102,
  people: 4102,
  sent: 5218,
  failed: 143,
  first_at: hoursFromNow(-3),
  last_at: hoursFromNow(-3),
  errors: [
    { code: 'UNREGISTERED', count: 121 },
    { code: 'UNAVAILABLE', count: 22 },
  ],
};

/** Il minimo di logica perché la conferma non sia una schermata vuota. */
function fakePreview(args: Record<string, unknown>): Preview {
  const title = String(args.p_title ?? '').trim();
  const body = String(args.p_body ?? '').trim();
  const push = args.p_push !== false;
  const revokes = args.p_revokes_id != null;
  const costs = push && !revokes;

  const errors: Preview['errors'] = [];
  if (!title) errors.push({ field: 'title', code: 'REQUIRED', message: 'Il titolo è obbligatorio.' });
  if (!body) errors.push({ field: 'body', code: 'REQUIRED', message: 'Il testo è obbligatorio.' });
  if (/allerta\s+(ross|arancion|giall|verd)/i.test(`${title} ${body}`)) {
    errors.push({
      field: 'body',
      code: 'FORBIDDEN_WORDING',
      message:
        'Il livello di allerta lo dichiara la Protezione Civile regionale. ' +
        'Scrivi cosa sta facendo il Comune — le scuole, la strada, il punto di raccolta.',
    });
  }

  const linkUrl = args.p_link_url ? String(args.p_link_url) : null;
  return {
    ok: errors.length === 0,
    errors,
    recipients: push ? SESSION.recipients : 0,
    card: {
      title,
      body,
      link_url: linkUrl,
      link_label: linkUrl
        ? String(args.p_link_label || '').trim() || 'Maggiori informazioni'
        : null,
    },
    push: push
      ? { title: `📣 ${SESSION.display_name}`, body: title, costs_quota: costs }
      : null,
    quota_after: {
      daily_used: SESSION.quota.daily_used + (costs ? 1 : 0),
      daily_cap: SESSION.quota.daily_cap,
      weekly_used: SESSION.quota.weekly_used + (costs ? 1 : 0),
      weekly_cap: SESSION.quota.weekly_cap,
    },
  };
}

/** Risponde al posto di Supabase. `undefined` = nessuna finzione per questa RPC. */
export function fixtureRpc(fn: string, args: Record<string, unknown>): unknown {
  switch (fn) {
    case 'municipal_session':
      return SESSION;
    case 'municipal_notices':
      return NOTICES;
    case 'municipal_notice_stats':
      return NOTICES.find((n) => n.id === args.p_id)?.push_requested
        ? STATS
        : ({ available: false, reason: 'no_dispatch' } satisfies Stats);
    case 'preview_comune_notice':
      return fakePreview(args);
    case 'publish_comune_notice':
      return { ok: true, id: NOTICES[0]?.id, errors: [] };
    case 'retract_comune_notice':
    case 'extend_comune_notice':
      return { ok: true, errors: [] };
    default:
      return undefined;
  }
}
